import { forwardRef } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface BackIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized back navigation icon button.
 */
export const BackIconButton = forwardRef<HTMLButtonElement, BackIconButtonProps>(
  ({ tooltip, showTooltip = true, className, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('app.back');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn('rtl:rotate-180', className)}
        {...props}
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

BackIconButton.displayName = 'BackIconButton';
